import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { intlShape, injectIntl } from 'react-intl';
import { withRouter } from 'react-router-dom';
import _ from 'lodash';
import * as Util from '../../../common/utilities';
import { PredictedWinnerBadge } from '../';

import {
  Badge,
  Card,
  DataTable,
  DataTableColumn,
  DataTableCell,
  Dropdown,
  Popover,
  Button,
  Tooltip
} from '@salesforce/design-system-react';

import { withFlags } from '../../../flags/Flags';

import './ExperimentAssetBrowser.scss';

const CustomDataTableCell = ({ children, property, ...props }) => {
  const { formatMessage, formatNumber } = props.intl;
  switch (property) {
    case 'thumbnail':
      const rowNumber = props.items.indexOf(props.item);
      let popoverAlign = 'right top';
      let position = 'relative';
      if (props.items.length - rowNumber < 5) {
        popoverAlign = 'right bottom';
        position = 'absolute';
      }
      let image_src = props.item.image_url;
      if (props.item.image_attributes) {
        props.item.image_attributes.image_data
          ? (image_src = props.item.image_attributes.image_data)
          : (image_src = props.item.image_attributes.image_url);
      }
      return (
        <td className={'thumbnail-cell'}>
          <Popover
            className={'thumbnail-expanded-popover'}
            heading={props.item.name}
            body={
              <div className="popover-body">
                <img
                  className="thumbnail-cell-popover-img"
                  src={image_src}
                  alt={formatMessage(
                    {
                      id: 'components.ecs.popover.image.altText'
                    },
                    {
                      name: props.item.name
                    }
                  )}
                />
                <div className="popover-text slds-grid slds-gutters">
                  <span className="popover-label slds-col slds-size_1-of-2">
                    {formatMessage({
                      id: 'components.experiment.assetBrowser.name'
                    })}
                  </span>
                  <span className="popover-value slds-col slds-size_2-of-2">
                    {props.item.name}
                  </span>
                </div>
              </div>
            }
            align={popoverAlign}
            position={
              props.popoverOverflow ? 'overflowBoundaryElement' : position
            }
          >
            <Button variant="base">
              <div
                className="thumbnail-container"
                style={{ backgroundImage: `url(${image_src})` }}
              />
            </Button>
          </Popover>
        </td>
      );
    case 'actions':
      const toggle = props.item.status === 'ACTIVE' ? 'disable' : 'enable';
      // The tooltip needs undefined for isOpen value, in order to open on hover
      const open = false;
      const actions = [
        {
          label: formatMessage({
            id: `components.experiment.assetBrowser.menu.${toggle}`
          }),
          tooltipContent: formatMessage({
            id: 'components.ecs.assetBrowser.tooltip'
          }),
          value: toggle
        }
      ];
      const activeRows = props?.experiment?.content_test_assets?.filter(
        function(asset) {
          return asset.status === 'ACTIVE';
        }
      );
      const disableMenu =
        activeRows && activeRows.length <= 1 && toggle === 'disable';
      return (
        <td className={'wrap-text'}>
          <Dropdown
            assistiveText={{ icon: 'Asset Options' }}
            buttonVariant="icon"
            iconCategory="utility"
            iconName="down"
            iconVariant="border-filled"
            align="right"
            className="actions-dropdown"
            width="xx-small"
            onSelect={({ value }) => props.onAction(value, props.item, false)}
            options={actions}
            disabled={disableMenu}
            tooltipMenuItem={
              <Tooltip
                id="drop-down-tooltip"
                align="left"
                position="overflowBoundaryElement"
                isOpen={open}
              />
            }
          />
        </td>
      );
    case 'selections':
    case 'clicks':
      let val = children || 0;
      val = formatNumber(val);
      return <td className="wrap-text">{val}</td>;
    case 'ctor':
      const percentTotal = props.item.clicks / props.item.selections || 0;
      return (
        <td className="wrap-text">
          {formatNumber(percentTotal, {
            style: 'percent',
            maximumFractionDigits: 2
          })}
        </td>
      );
    case 'name':
      return (
        <td className="wrap-text asset-name">
          {children}
          {props.item?.isPredictedWinner ? <PredictedWinnerBadge /> : null}
        </td>
      );
    case 'redirect_url':
      return <td className="wrap-text asset-url">{children}</td>;
    case 'isDisabled':
      if (props.item.status === 'INACTIVE') {
        return (
          <td className="wrap-text disable-badge">
            <Badge
              id="badge-base-example-inverse"
              color="inverse"
              content={formatMessage({
                id: 'components.experiment.assetBrowser.disabled'
              })}
            />
          </td>
        );
      }
      return <td className="wrap-text" />;
    default: {
      let val = children;
      if (props.numberStyle) {
        const { formatNumber } = props.intl;
        val = formatNumber(children, props.numberStyle);
      }
      return <td className="wrap-text">{val}</td>;
    }
  }
};

CustomDataTableCell.displayName = DataTableCell.displayName;
CustomDataTableCell.propTypes = {
  children: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
    PropTypes.object,
    PropTypes.array
  ]),
  property: PropTypes.string.isRequired,
  onclick: PropTypes.func,
  onAction: PropTypes.func,
  formatMessage: PropTypes.func,
  item: PropTypes.object,
  numberStyle: PropTypes.object,
  intl: intlShape
};

class ExperimentAssetBrowser extends Component {
  constructor(props) {
    super(props);
    this.assetTable = React.createRef();
  }

  getI18nID(suffix) {
    return `components.experiment.assetBrowser.${suffix}`;
  }

  headerCell(property, isCustom, formatMessage, tooltip) {
    const text = isCustom
      ? property
      : formatMessage({ id: this.getI18nID(property) });
    if (tooltip) {
      const tooltipText = formatMessage({ id: tooltip });
      return (
        <span>
          {text}
          <Tooltip
            align="right"
            content={tooltipText}
            position="overflowBoundaryElement"
          />
        </span>
      );
    }

    return text;
  }

  columns(flags) {
    const defaultWidth = '6.25rem';
    const buildColumn = (
      name,
      width,
      custom,
      tooltip,
      sortable,
      sortDirection
    ) => ({
      name,
      sortable: sortable || false,
      sortDirection: sortDirection || undefined,
      width: width || defaultWidth,
      custom,
      tooltip
    });

    const sortDirection = key =>
      this.props.sortColumn?.property === key &&
      this.props.sortColumn?.sortDirection;

    const columns = [];
    if (this.props.columns?.length) {
      columns.push(...this.props.columns);
    } else {
      columns.push(buildColumn('thumbnail', '62px'));

      const defaultColumns = [
        'thumbnail',
        'name',
        'selections',
        'clicks',
        'ctor'
      ];
      columns.push(
        ...defaultColumns.map(key => {
          const width = key === 'key' ? '7rem' : undefined;
          return buildColumn(
            key,
            width,
            undefined,
            undefined,
            this.props.sortable || false,
            sortDirection(key)
          );
        })
      );
    }
    if (this.props.showActions && !(flags && flags.isReadonly)) {
      columns.push(buildColumn('actions', '50px'));
    }

    if (this.props.keys?.length) {
      const customKeys = this.props.keys.map(key =>
        buildColumn(key, defaultWidth, true)
      );
      columns.push(...customKeys);
    }

    return columns.reduce((obj, item) => {
      obj[item.name] = item;
      return obj;
    }, {});
  }

  shouldComponentUpdate(nextProps, nextState, nextContext) {
    /**
     * Only update this component if: we've never rendered or one of its props changes
     *
     * This code prevents some major input lag when typing into the AssetEditWizard because
     * every change (ie keypress) in the form would otherwise re-render this whole component
     */
    if (this.hasRendered) {
      const selectionChanged = !_.isEqual(
        this.props.selection,
        nextProps.selection
      );
      const assetsChanged = !_.isEqual(this.props.assets, nextProps.assets);
      const scrollToTopChanged =
        this.props.scrollToTop !== nextProps.scrollToTop;
      return selectionChanged || assetsChanged || scrollToTopChanged;
    }
    return true;
  }

  componentDidUpdate(prevProps) {
    if (Util.propChanged(prevProps, this.props, 'scrollToTop', true)) {
      if (this.assetTable.current?.scrollerRef) {
        this.assetTable.current.scrollerRef.scrollTo({ top: 0 });
        if (this.props.onScrollToTop) {
          this.props.onScrollToTop();
        }
      }
    }
  }

  tableData() {
    if (this.props.assets) {
      return this.props.assets.map(item => ({
        ...item,
        ...item.custom,
        id: item?.uuid?.toString()
      }));
    }
    return [];
  }

  renderTable(items) {
    const { formatMessage } = this.props.intl;
    return withFlags(flags => {
      let selection = this.props.selection;
      if (selection) {
        const selectedKeys = selection.map(s => s.uuid);
        selection = items.filter(item => selectedKeys.includes(item.uuid));
      }
      const columns = this.columns(flags);
      const sortFunc = this.props.onSort ? this.props.onSort : () => {};
      return (
        <Card hasNoHeader id="table-wrapper">
          <div className={'experiment-asset-browser'}>
            <DataTable
              items={items}
              className={'experiment-asset-table'}
              selection={selection}
              selectRows={this.props.rowSelectionMode}
              onRowChange={this.props.onRowSelect}
              fixedHeader
              fixedLayout
              ref={this.assetTable}
              onSort={sortFunc}
            >
              {Object.entries(columns).map((column, index) => {
                const sortable = column[1].sortable;
                const sortDirection = column[1].sortDirection;
                const width = column[1].width;
                const custom = column[1].custom;
                const numberStyle = column[1].numberStyle;
                const tooltip = column[1].tooltip;
                const property = column[0];
                return (
                  <DataTableColumn
                    key={index}
                    sortable={sortable}
                    isSorted={!!sortDirection}
                    sortDirection={sortDirection}
                  >
                    <CustomDataTableCell
                      experiment={this.props.experiment}
                      property={property}
                      formatMessage={formatMessage}
                      label={this.headerCell(
                        property,
                        custom,
                        formatMessage,
                        tooltip
                      )}
                      width={width}
                      numberStyle={numberStyle}
                      intl={this.props.intl}
                      popoverOverflow={this.props.popoverOverflow}
                      onAction={this.props.onAction}
                      flags={flags}
                    />
                  </DataTableColumn>
                );
              })}
            </DataTable>
          </div>
        </Card>
      );
    });
  }

  render() {
    this.hasRendered = true;
    return this.renderTable(this.tableData());
  }
}

ExperimentAssetBrowser.propTypes = {
  assets: PropTypes.array,
  experiment: PropTypes.object,
  rowSelectionMode: PropTypes.oneOf(['radio', 'checkbox']),
  selection: PropTypes.array,
  onLoadMore: PropTypes.func,
  onRowSelect: PropTypes.func,
  onAction: PropTypes.func,
  onScrollToTop: PropTypes.func,
  scrollToTop: PropTypes.bool,
  popoverOverflow: PropTypes.bool,
  keys: PropTypes.array,
  sortable: PropTypes.bool,
  sortColumn: PropTypes.object,
  onSort: PropTypes.func,
  columns: PropTypes.array,
  showActions: PropTypes.bool,
  location: PropTypes.object,
  intl: intlShape.isRequired
};

export default injectIntl(withRouter(ExperimentAssetBrowser));
